import { type PropsWithChildren, useEffect } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import type { ImsApi } from '@/api/ImsApi.ts';
import { useImsApiContext } from '@/auth/ImsApiContext.tsx';
import { ImsTokenContext, UserIdContext } from '@/auth/UserIdContext';
import { LoadingInterstitial } from '@/components/LoadingInterstitial.tsx';
import { MobileBlocker } from '@/components/MobileBlocker/MobileBlocker';
import { RegionBlocked } from '@/components/RegionBlocked/RegionBlocked';
import { Login, markAsInvited } from '@/screens/login/Login.tsx';

const AccessDenied = ({
	email,
	imsApi,
}: {
	email: string;
	imsApi: { logout: () => void };
}) => (
	<div
		style={{ padding: '2rem', fontFamily: 'system-ui', textAlign: 'center' }}
	>
		<h2>Access Denied</h2>
		<p>{email} is not authorized.</p>
		<button type="button" onClick={() => imsApi.logout()}>
			Sign out
		</button>
	</div>
);

/**
 * Redirects to IMS auth on mount instead of showing the landing page.
 * Used by developer/demo routes where the landing page is never useful.
 */
const AutoRedirect = ({ imsApi }: { imsApi: ImsApi }) => {
	useEffect(() => {
		imsApi.redirectToImsAuth();
	}, [imsApi]);
	return <LoadingInterstitial />;
};

export const AuthGate = ({
	children,
	autoRedirect,
	allowMobile,
	allowWithoutUserState,
}: PropsWithChildren<{
	autoRedirect?: boolean;
	allowMobile?: boolean;
	allowWithoutUserState?: boolean;
}>) => {
	const bridgeApi = useBridgeApi();
	const imsApiContext = useImsApiContext();
	const imsApi = imsApiContext?.imsApi;
	const imsUserId = imsApiContext?.userId;
	const imsToken = imsApiContext?.imsToken;
	const sessionDeadline = imsApiContext?.sessionDeadline ?? null;
	const expireSession = imsApiContext?.expireSession;
	const imsLoading = imsApiContext?.loading ?? true;

	useEffect(() => {
		bridgeApi.setImsToken(imsToken ?? null, imsUserId ?? null, sessionDeadline);
	}, [bridgeApi, imsToken, imsUserId, sessionDeadline]);

	useEffect(() => {
		bridgeApi.setSessionExpiredHandler(expireSession ?? null);
		return () => bridgeApi.setSessionExpiredHandler(null);
	}, [bridgeApi, expireSession]);

	useEffect(() => {
		if (imsUserId && imsToken) {
			markAsInvited();
		}
	}, [imsUserId, imsToken]);

	if (!imsApi) {
		return null;
	}

	// While the IMS provider is still starting up (reading the token from the
	// auth callback, fetching the profile, checking the allowlist), wait on a
	// loading interstitial instead of falling through to the landing page.
	if (imsLoading) {
		return <LoadingInterstitial delayMs={0} />;
	}

	// Accounts from regions where Beacon is not offered never receive a
	// userId/token, so this must sit above the signed-out branch — otherwise
	// they would land on the login screen and loop. Sitting first also means
	// the block wins even if some future path publishes a token for a
	// blocked account.
	if (imsApiContext?.blockedCountry) {
		return <RegionBlocked imsApi={imsApi} />;
	}

	if (!imsUserId || !imsToken) {
		if (imsApiContext?.deniedEmail) {
			return <AccessDenied email={imsApiContext.deniedEmail} imsApi={imsApi} />;
		}

		if (autoRedirect) {
			return <AutoRedirect imsApi={imsApi} />;
		}

		const showLogout = !!imsApi.getCachedAccessToken();
		return (
			<Login
				imsApi={imsApi}
				showLogout={showLogout}
				onLogout={() => imsApi.logout()}
			/>
		);
	}

	// This wait has no timeout, no retry cap and no error branch, and
	// `BridgeApi` caps the reconnect DELAY rather than the attempt count. A page
	// that never reads `user_state` must not sit behind it, or a customer who
	// taps a link in a text can watch a spinner for ever with nothing to do.
	if (!allowWithoutUserState && !bridgeApi.user_state) {
		return <LoadingInterstitial />;
	}

	return (
		<UserIdContext.Provider value={imsUserId}>
			<ImsTokenContext.Provider value={imsToken}>
				{allowMobile ? children : <MobileBlocker>{children}</MobileBlocker>}
			</ImsTokenContext.Provider>
		</UserIdContext.Provider>
	);
};
